// /vs/khanmigo and /vs/magicschool — reusable comparison page (D5.3).
import { SEO } from "@/components/SEO";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, X, Zap } from "lucide-react";

const COMPETITORS = {
  starspark: {
    name: "StarSpark AI",
    parent: "StarSpark AI",
    price: "$25/mo parent",
    tagline: "Adaptive AI tutor with 23,000+ lessons",
    seoTitle: "Lectura vs StarSpark AI: Chapter-to-Study Package vs Adaptive Practice",
    seoDesc: "Lectura vs StarSpark AI compared. Lectura ships lecture + slides + guide + quiz per textbook chapter at $7.99/mo. StarSpark offers 23,000+ lessons + adaptive practice at $25/mo.",
    pitch: "StarSpark AI has a huge content library and adaptive practice. Lectura turns your own textbook chapter into a complete study package, at a third of the price.",
    rows: [
      { feature: "Works from your own textbook chapter", us: true, them: false, note: "StarSpark uses its own 23,000+ lesson library." },
      { feature: "AI-narrated lecture (10-15 min) per chapter", us: true, them: false, note: "" },
      { feature: "Printable study guide with citations", us: true, them: false, note: "" },
      { feature: "Slide deck generation", us: true, them: false, note: "" },
      { feature: "Graded quiz with letter grade + explanations", us: true, them: "Adaptive practice", note: "" },
      { feature: "Live Class Game Mode for classrooms", us: true, them: false, note: "" },
      { feature: "Adaptive practice with 240,000+ problems", us: false, them: true, note: "StarSpark's strength." },
      { feature: "Content library size", us: "Any textbook", them: "23,000+ lessons", note: "" },
      { feature: "Student self-serve pricing", us: "$7.99/mo", them: "$11.99/mo student", note: "" },
      { feature: "Parent / family pricing", us: "$7.99/mo", them: "$25/mo parent", note: "" },
      { feature: "Free tier", us: "3 lectures", them: "Limited", note: "" },
      { feature: "FERPA / COPPA compliant", us: true, them: true, note: "" },
      { feature: "Standards-aligned (Common Core, NGSS, TEKS)", us: true, them: true, note: "" },
    ],
    bottomLine: "Pick StarSpark for adaptive practice with a large content library. Pick Lectura for a complete study package from any textbook chapter at a third of the price.",
  },
  khanmigo: {
    name: "Khanmigo",
    parent: "Khan Academy",
    price: "$4/mo",
    tagline: "Conversational AI tutor",
    seoTitle: "Lectura vs Khanmigo: Chapter-to-Lecture vs Conversational Tutor",
    seoDesc: "Lectura vs Khanmigo compared. Lectura ships a full lecture + slides + printable guide + quiz per chapter. Khanmigo is chat-only inside Khan's library.",
    pitch: "Khanmigo is a great conversational tutor. But conversation is slow when you need to master a whole chapter. Lectura hands you the full lesson package in one shot.",
    rows: [
      { feature: "AI-narrated lecture (10-15 min)", us: true, them: false, note: "Khanmigo is chat-only; no lecture output." },
      { feature: "Slide deck generation", us: true, them: false, note: "" },
      { feature: "Printable study guide", us: true, them: false, note: "" },
      { feature: "Graded quiz with letter grade", us: true, them: false, note: "Khanmigo does quiz-style Q&A within chats." },
      { feature: "Ask follow-up questions", us: true, them: true, note: "Both support conversational follow-ups." },
      { feature: "Live Class Game Mode", us: true, them: false, note: "" },
      { feature: "Works with any textbook, any chapter", us: true, them: false, note: "Khanmigo is bounded to Khan Academy's curriculum." },
      { feature: "Bilingual (English/Spanish)", us: true, them: true, note: "" },
      { feature: "Free tier", us: "3 lectures", them: "Limited", note: "" },
      { feature: "Pro price", us: "$7.99/mo", them: "$4/mo", note: "Khanmigo cheaper; different scope of output." },
      { feature: "FERPA-Ready", us: true, them: true, note: "" },
      { feature: "Standards-aligned (Common Core, NGSS, TEKS)", us: true, them: true, note: "" },
    ],
    bottomLine: "Pick Khanmigo if you want a friendly chatbot inside Khan Academy's curriculum. Pick Lectura if you want a full study package — lecture + slides + guide + quiz — from any textbook you own.",
  },
  magicschool: {
    name: "MagicSchool",
    parent: "MagicSchool.ai",
    price: "$99.96/yr Plus",
    tagline: "80+ teacher tools",
    seoTitle: "Lectura vs MagicSchool: One Workflow vs 80+ Tools",
    seoDesc: "Lectura vs MagicSchool. Lectura runs one workflow per chapter. MagicSchool bundles 80+ separate tools, teacher-only Plus tier. See the full comparison.",
    pitch: "MagicSchool has 80+ separate tools you have to stitch together. Lectura runs the whole study pipeline from one input — and it's for students, not just teachers.",
    rows: [
      { feature: "One integrated workflow (single input)", us: true, them: false, note: "MagicSchool is 80+ separate tools." },
      { feature: "Student self-serve pricing", us: "$7.99/mo", them: false, note: "MagicSchool Plus is teacher-only." },
      { feature: "AI-narrated lecture", us: true, them: false, note: "" },
      { feature: "Slide deck generation", us: true, them: "Separate tool", note: "" },
      { feature: "Printable study guide", us: true, them: "Separate tool", note: "" },
      { feature: "Graded quiz with letter grade", us: true, them: "Separate tool", note: "" },
      { feature: "Live Class Game Mode", us: true, them: false, note: "" },
      { feature: "Classroom rostering (teacher tier)", us: true, them: true, note: "" },
      { feature: "Standards alignment (Common Core / NGSS / TEKS)", us: true, them: true, note: "" },
      { feature: "SOC 2 Type II certified", us: false, them: true, note: "Lectura on roadmap." },
      { feature: "Ed Law 2-D (NY)", us: false, them: true, note: "Lectura on roadmap." },
      { feature: "FERPA / COPPA", us: true, them: true, note: "" },
    ],
    bottomLine: "Pick MagicSchool if you're a teacher who needs 80+ specialized tools and you have district approval. Pick Lectura if you want one workflow that turns any chapter into a complete lesson package, priced for students too.",
  },
};

export default function VsPage() {
  const { slug } = useParams();
  const data = COMPETITORS[slug];
  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO title={data.seoTitle} description={data.seoDesc} canonical={`https://getlectura.app/vs/${slug}`} />
      <MarketingNav />
      <main>
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-accent">Comparison</p>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold">
              Lectura vs {data.name}
            </h1>
            <p className="mt-4 text-muted-foreground">{data.pitch}</p>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-4 sm:px-6 py-12">
          <div className="overflow-x-auto rounded-2xl border border-border/60">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Feature</th>
                  <th className="px-4 py-3 font-semibold text-accent">Lectura</th>
                  <th className="px-4 py-3 font-semibold text-muted-foreground">{data.name}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {data.rows.map((row, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{row.feature}</div>
                      {row.note && <div className="text-xs text-muted-foreground mt-0.5">{row.note}</div>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.us === true ? <Check className="inline h-4 w-4 text-accent" /> : row.us === false ? <X className="inline h-4 w-4 text-muted-foreground/50" /> : <span className="font-semibold text-accent">{row.us}</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.them === true ? <Check className="inline h-4 w-4 text-muted-foreground" /> : row.them === false ? <X className="inline h-4 w-4 text-muted-foreground/50" /> : <span className="font-medium text-muted-foreground">{row.them}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 rounded-2xl bg-secondary/40 p-6">
            <p className="font-semibold">Bottom line</p>
            <p className="mt-2 text-sm text-muted-foreground">{data.bottomLine}</p>
          </div>

          <div className="mt-10 text-center">
            <Link to="/register" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-bold">
              Try Lectura free <Zap className="h-4 w-4" />
            </Link>
            <p className="mt-3 text-xs text-muted-foreground">3 free lectures. No credit card required.</p>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
